import {datadogRum, RumEvent} from '@datadog/browser-rum';

import {environment} from '@environments/environment';
import {collectorUrlPattern, stripQueryString, stripViewUrls} from './telemetry.util';

/**
 * Sentinel applicationId meaning "no real RUM app configured". While the configured applicationId
 * is unset or still equals this value, `initDatadogRum` no-ops so RUM never starts against a
 * bogus application.
 */
export const RUM_APPLICATION_ID_PLACEHOLDER = 'YOUR_DATADOG_RUM_APP_ID_HERE';

/**
 * Initializes Datadog Browser RUM for page-load / view timing. Automatic view tracking populates
 * `@view.loading_time` per route, which the SLA monitors aggregate.
 *
 * Reuses the same browser client token as `datadogLogs` (RUM and Logs share the token), fetched at
 * runtime from `/v2/api/public-config/frontend-datadog-client-token`. The applicationId is
 * non-secret and comes from `environment.datadog`.
 *
 * No-op — never throws — when RUM is disabled, the applicationId is unset/placeholder, or the
 * client token is empty, so a missing RUM application can never break bootstrapping.
 */
export function initDatadogRum(clientToken: string): void {
  const dd = environment.datadog;
  if (!dd.rumEnabled) {
    return;
  }
  if (!dd.applicationId || dd.applicationId === RUM_APPLICATION_ID_PLACEHOLDER) {
    console.warn('[Datadog RUM] applicationId not configured; RUM is disabled.');
    return;
  }
  if (!clientToken) {
    return;
  }
  // The OTel exporter POSTs batches to the collector (`${otelCollectorUrl}/v1/traces`). Exclude that
  // endpoint from RUM page-activity tracking (shared pattern with otel.ts) so those POSTs aren't
  // treated as page activity and can't inflate `@view.loading_time`.
  //
  // `trackResources` is on  so RUM can see the app's own API
  // calls and, combined with `allowedTracingUrls` below, link them to backend APM traces -- without
  // this, Datadog's service dependency map for the frontend has no data at all. `allowedTracingUrls`
  // is scoped to `apiBaseUrl` rather than the whole origin so Datadog tracing headers are only
  // injected into the app's own API calls -- not the OTel collector's requests or other same-origin
  // traffic, keeping it consistent with this file excluding the collector from RUM. The collector's
  // own POSTs are also dropped as resource EVENTS in `beforeSend` below (not just excluded from
  // activity tracking) -- otherwise re-enabling `trackResources` would reintroduce exactly the noise
  // this file originally turned it off to avoid.
  const collectorPattern = collectorUrlPattern();
  const apiUrlPrefix = window.location.origin + environment.apiBaseUrl;
  datadogRum.init({
    applicationId: dd.applicationId,
    clientToken,
    site: dd.site,
    service: dd.service,
    env: environment.env,
    sessionSampleRate: dd.sessionSampleRate,
    sessionReplaySampleRate: 0,
    trackResources: true,
    trackLongTasks: false,
    trackUserInteractions: false,
    allowedTracingUrls: [(url: string): boolean => url.startsWith(apiUrlPrefix)],
    excludedActivityUrls: [collectorPattern],
    beforeSend: (event: RumEvent): boolean => {
      // Drop the collector's own resource events entirely -- see the trackResources comment above.
      // Must come before the sanitization below since a dropped event needs no further processing.
      if (event.type === 'resource' && event.resource?.url && collectorPattern.test(event.resource.url)) {
        return false;
      }
      stripViewUrls(event.view);
      if (event.type === 'error' && event.error.resource) {
        event.error.resource.url = stripQueryString(event.error.resource.url);
      } else if (event.type === 'resource' && event.resource?.url) {
        event.resource.url = stripQueryString(event.resource.url);
      }
      return true;
    },
    defaultPrivacyLevel: 'mask-user-input',
  });
}
